import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import StaffSidebarLayout from "./StaffSidebarLayout";
import FreelancerSidebarLayout from "../freelancer/FreelancerSidebarLayout";

const WaitlistManagement = ({ roleType = "professional" }) => {
  // Determine the sidebar layout and booking path based on role
  const isFreelancerRole = roleType === "freelancer" || roleType === "booth-renter";
  const navigate = useNavigate();
  const [waitlist, setWaitlist] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [manualWaitlistActionsEnabled, setManualWaitlistActionsEnabled] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    staffId: "",
    serviceId: "",
  });

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }, [navigate]);

  // Fetch waitlist and related data
  const fetchData = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      // Get user's shop ID from staff record (supports all staff roles)
      const userResponse = await fetch(`${API_BASE_URL}/api/staff/profile`, {
        headers,
      });

      if (!userResponse.ok) {
        alert("Unable to load staff profile");
        return;
      }

      const userData = await userResponse.json();
      const activeLocation = userData.staff?.activeLocationId;
      const shopId =
        (activeLocation && typeof activeLocation === "object"
          ? activeLocation._id
          : activeLocation) ||
        userData.staff?.shopId;

      if (!shopId) {
        alert("No active shop location found for your account");
        return;
      }

      // Build query params
      const params = new URLSearchParams();
      if (filters.status) params.append("status", filters.status);
      if (filters.staffId) params.append("staffId", filters.staffId);
      if (filters.serviceId) params.append("serviceId", filters.serviceId);

      // Fetch waitlist
      const waitlistResponse = await fetch(
        `${API_BASE_URL}/api/waitlist/shop/${shopId}?${params.toString()}`,
        { headers },
      );
      const waitlistData = await waitlistResponse.json();
      setWaitlist(waitlistData.waitlist || []);
      setManualWaitlistActionsEnabled(waitlistData.manualWaitlistActionsEnabled === true);

      // Fetch services
      const servicesResponse = await fetch(
        `${API_BASE_URL}/api/staff/services`,
        { headers },
      );
      const servicesData = await servicesResponse.json();
      setServices(servicesData.services || []);

      // Don't fetch staff list - professionals can only see their own waitlist
      // Staff filtering is handled by the backend based on authentication
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, getAuthHeaders, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update priority
  const handlePriorityChange = async (waitlistId, newPriority) => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/waitlist/${waitlistId}/priority`,
        {
          method: "PATCH",
          headers,
          body: JSON.stringify({ priority: parseInt(newPriority) }),
        },
      );

      if (response.ok) {
        fetchData();
      }
    } catch (error) {
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "N/A";

    const rawValue = String(dateValue);
    const dateOnlyMatch = rawValue.match(/^(\d{4}-\d{2}-\d{2})/);
    const date = dateOnlyMatch
      ? new Date(`${dateOnlyMatch[1]}T12:00:00`)
      : new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return "N/A";
    }

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    const labels = {
      pending: "Pending",
      active: "Active",
      notified: "Notified",
      booked: "Booked",
      closed_booked_occurred: "Closed (Booked Occurred)",
      closed_cancelled: "Closed (Cancelled)",
      closed_fallback_mismatch: "Closed (Fallback Mismatch)",
      expired: "Expired",
      cancelled: "Cancelled",
    };
    const colors = {
      pending: { bg: "#ede9fe", text: "#5b21b6" },
      active: { bg: "#dbeafe", text: "#1e40af" },
      notified: { bg: "#fef3c7", text: "#92400e" },
      booked: { bg: "#d1fae5", text: "#065f46" },
      closed_booked_occurred: { bg: "#ede9fe", text: "#5b21b6" },
      closed_cancelled: { bg: "#fee2e2", text: "#991b1b" },
      closed_fallback_mismatch: { bg: "#ffedd5", text: "#9a3412" },
      expired: { bg: "#f3f4f6", text: "#4b5563" },
      cancelled: { bg: "#fee2e2", text: "#991b1b" },
    };
    const color = colors[status] || colors.active;
    return (
      <div
        style={{
          background: color.bg,
          color: color.text,
          padding: "4px 12px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: "600",
          display: "inline-block",
        }}
      >
        {labels[status] || String(status || "Unknown")}
      </div>
    );
  };

  const getPriorityBadge = (priority) => {
    const levels = {
      1: { label: "Low", bg: "#f3f4f6", text: "#4b5563" },
      2: { label: "Medium", bg: "#fef3c7", text: "#92400e" },
      3: { label: "High", bg: "#fed7aa", text: "#9a3412" },
      4: { label: "Urgent", bg: "#fecaca", text: "#991b1b" },
    };
    const level = levels[priority] || levels[1];
    return (
      <div
        style={{
          background: level.bg,
          color: level.text,
          padding: "4px 12px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: "600",
          display: "inline-block",
        }}
      >
        {level.label}
      </div>
    );
  };

  const SidebarLayout = isFreelancerRole ? FreelancerSidebarLayout : StaffSidebarLayout;
  const layoutProps = isFreelancerRole ? {} : { roleType: "professional" };

  if (loading) {
    return (
      <SidebarLayout {...layoutProps}>
        <div style={{ padding: "24px", textAlign: "center" }}>
          Loading waitlist...
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout {...layoutProps}>
      <div style={{ padding: window.innerWidth > 768 ? "24px" : "16px" }}>
        {/* Header */}
        <div style={{ marginBottom: "24px" }}>
          <h1
            style={{
              fontSize: "28px",
              fontWeight: "700",
              margin: 0,
              marginBottom: "8px",
            }}
          >
            📋 Waitlist Management
          </h1>
          <p style={{ color: "#6b7280", margin: 0 }}>
            Monitor pending and active customer waitlist requests
          </p>
        </div>

        {/* Filters */}
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            padding: "24px",
            marginBottom: "24px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                window.innerWidth > 768 ? "repeat(3, 1fr)" : "1fr",
              gap: "16px",
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "600",
                  marginBottom: "8px",
                }}
              >
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, status: e.target.value }))
                }
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "8px",
                  border: "2px solid #e5e7eb",
                  fontSize: "14px",
                }}
              >
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="active">Active</option>
                <option value="notified">Notified</option>
                <option value="booked">Booked</option>
                <option value="closed_booked_occurred">Closed (Booked Occurred)</option>
                <option value="closed_cancelled">Closed (Cancelled)</option>
                <option value="expired">Expired</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "600",
                  marginBottom: "8px",
                }}
              >
                Service
              </label>
              <select
                value={filters.serviceId}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, serviceId: e.target.value }))
                }
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "8px",
                  border: "2px solid #e5e7eb",
                  fontSize: "14px",
                }}
              >
                <option value="">All Services</option>
                {services.map((service) => (
                  <option key={service._id} value={service._id}>
                    {service.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Staff filter removed - professionals only see their own waitlist */}
          </div>
        </div>

        {/* Waitlist entries */}
        {waitlist.length === 0 ? (
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "48px",
              textAlign: "center",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>📋</div>
            <h2
              style={{
                fontSize: "20px",
                fontWeight: "600",
                marginBottom: "8px",
              }}
            >
              No waitlist entries
            </h2>
            <p style={{ color: "#6b7280" }}>
              Customers can join the waitlist when services are fully booked
            </p>
          </div>
        ) : (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "16px" }}
          >
            {waitlist.map((entry) => (
              <div
                key={entry._id}
                style={{
                  background: "white",
                  borderRadius: "12px",
                  padding: "24px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  border: "2px solid #e5e7eb",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "start",
                    marginBottom: "16px",
                    flexWrap: "wrap",
                    gap: "16px",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        marginBottom: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      <h3
                        style={{
                          fontSize: "18px",
                          fontWeight: "700",
                          margin: 0,
                        }}
                      >
                        {entry.customerName ||
                          `${entry.customerFirstName || ""} ${entry.customerLastName || ""}`.trim() ||
                          `${entry.customerId?.firstName || ""} ${entry.customerId?.lastName || ""}`.trim() ||
                          "Unknown Customer"}
                      </h3>
                      {getStatusBadge(entry.status)}
                      {getPriorityBadge(entry.priority)}
                    </div>
                    <div style={{ fontSize: "14px", color: "#6b7280" }}>
                      📧 {entry.customerEmail || entry.customerId?.email || "N/A"} • 📞{" "}
                      {entry.customerPhone || entry.customerId?.phone || "N/A"}
                    </div>
                  </div>

                  {manualWaitlistActionsEnabled && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <label
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#6b7280",
                        }}
                      >
                        Priority:
                      </label>
                      <select
                        value={entry.priority}
                        onChange={(e) =>
                          handlePriorityChange(entry._id, e.target.value)
                        }
                        disabled={
                          entry.status !== "active" && entry.status !== "notified"
                        }
                        style={{
                          padding: "6px 12px",
                          borderRadius: "6px",
                          border: "2px solid #e5e7eb",
                          fontSize: "13px",
                          fontWeight: "600",
                        }}
                      >
                        <option value="1">Low</option>
                        <option value="2">Medium</option>
                        <option value="3">High</option>
                        <option value="4">Urgent</option>
                      </select>
                    </div>
                  )}
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      window.innerWidth > 1024
                        ? "repeat(3, 1fr)"
                        : window.innerWidth > 768
                          ? "repeat(2, 1fr)"
                          : "1fr",
                    gap: "16px",
                    marginBottom: "16px",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#4b5563",
                        marginBottom: "4px",
                      }}
                    >
                      Service
                    </div>
                    <div style={{ fontSize: "14px" }}>
                      {entry.serviceId?.name || entry.serviceBundle?.[0]?.name || "Any service"}
                    </div>
                  </div>

                  <div>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#4b5563",
                        marginBottom: "4px",
                      }}
                    >
                      Total Service Duration
                    </div>
                    <div style={{ fontSize: "14px" }}>
                      {Number(entry.totalServiceDurationMinutes || 0) > 0
                        ? `${Number(entry.totalServiceDurationMinutes)} min`
                        : `${entry.serviceId?.duration || "N/A"} min`}
                    </div>
                  </div>

                  <div>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#4b5563",
                        marginBottom: "4px",
                      }}
                    >
                      Joined Waitlist
                    </div>
                    <div style={{ fontSize: "14px" }}>
                      {formatDate(entry.createdAt)}
                    </div>
                  </div>
                </div>

                {/* Preferred dates/times */}
                {Array.isArray(entry.serviceBundle) && entry.serviceBundle.length > 0 && (
                  <div style={{ marginBottom: "16px" }}>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#4b5563",
                        marginBottom: "8px",
                      }}
                    >
                      Selected Services
                    </div>
                    <div style={{ display: "grid", gap: "8px" }}>
                      {entry.serviceBundle.map((svc, idx) => (
                        <div
                          key={`${svc.serviceId || svc.name || "svc"}-${idx}`}
                          style={{
                            background: "#f8fafc",
                            border: "1px solid #e5e7eb",
                            borderRadius: "8px",
                            padding: "8px 10px",
                            fontSize: "13px",
                            color: "#1f2937",
                          }}
                        >
                          <strong>{svc.name || "Service"}</strong>
                          {Number(svc.durationMinutes || 0) > 0
                            ? ` • ${Number(svc.durationMinutes)} min`
                            : ""}
                          {Number(svc.price || 0) > 0
                            ? ` • $${Number(svc.price).toFixed(2)}`
                            : ""}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {entry.preferredDates && entry.preferredDates.length > 0 && (
                  <div style={{ marginBottom: "16px" }}>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#4b5563",
                        marginBottom: "8px",
                      }}
                    >
                      Preferred Dates
                    </div>
                    <div
                      style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}
                    >
                      {entry.preferredDates.map((date, idx) => (
                        <div
                          key={idx}
                          style={{
                            background: "#f3f4f6",
                            padding: "6px 12px",
                            borderRadius: "6px",
                            fontSize: "13px",
                          }}
                        >
                          {formatDate(date)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {entry.preferredTimeSlots &&
                  entry.preferredTimeSlots.length > 0 && (
                    <div style={{ marginBottom: "16px" }}>
                      <div
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#4b5563",
                          marginBottom: "8px",
                        }}
                      >
                        Preferred Time Slots
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          flexWrap: "wrap",
                        }}
                      >
                        {entry.preferredTimeSlots.map((slot, idx) => (
                          <div
                            key={idx}
                            style={{
                              background: "#f3f4f6",
                              padding: "6px 12px",
                              borderRadius: "6px",
                              fontSize: "13px",
                            }}
                          >
                            {typeof slot === "string"
                              ? slot
                              : `${slot?.startTime || ""}${slot?.endTime ? ` - ${slot.endTime}` : ""}`}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Notification info */}
                {entry.notificationCount > 0 && (
                  <div
                    style={{
                      background: "#fef3c7",
                      padding: "12px",
                      borderRadius: "8px",
                      marginBottom: "16px",
                    }}
                  >
                    <div style={{ fontSize: "13px", color: "#92400e" }}>
                      📬 Notified {entry.notificationCount} time(s) • Last:{" "}
                      {formatDate(entry.lastNotificationAt)}
                    </div>
                  </div>
                )}

                <div
                  style={{
                    marginTop: "4px",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    background: "#f8fafc",
                    color: "#475569",
                    fontSize: "13px",
                  }}
                >
                  Automated flow: waitlist alerts are handled by the system based on activation and matching rules.
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </SidebarLayout>
  );
};

export default WaitlistManagement;
